import React, { useMemo } from 'react';
import { Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { BlueButton } from '@share/components';

import { formatShortUsDate, type PaymentScheduleEntry } from '../../utils/payment-math';

import styles from './styles.module.scss';

export interface PaymentScheduleModalProps {
  open: boolean;
  onClose: () => void;
  entries: PaymentScheduleEntry[];
  total: number;
}

export function PaymentScheduleModal({
  open,
  onClose,
  entries,
  total,
}: PaymentScheduleModalProps): React.ReactElement {
  const intl = useIntl();

  const rows = useMemo(
    () =>
      entries.map((e) => ({
        key: String(e.rowNumber),
        num: e.rowNumber,
        payment: e.paymentDescription,
        date: formatShortUsDate(e.date),
        amount: e.amount,
        isDeposit: e.isDeposit,
      })),
    [entries],
  );

  return (
    <Modal
      className={styles.modal}
      visible={open}
      title={intl.formatMessage({ id: 'certificates.payment.scheduleModal.title' })}
      onCancel={onClose}
      footer={
        <div className={styles.footer}>
          <BlueButton className={styles.closeButton} onClick={onClose}>
            {intl.formatMessage({ id: 'certificates.payment.scheduleModal.close' })}
          </BlueButton>
        </div>
      }
      closable
      centered
      width={560}
    >
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col" className={styles.thNum}>
                <FormattedMessage id="certificates.payment.scheduleModal.colHash" />
              </th>
              <th scope="col" className={styles.thPayment}>
                <FormattedMessage id="certificates.payment.scheduleModal.colPayment" />
              </th>
              <th scope="col" className={styles.thDate}>
                <FormattedMessage id="certificates.payment.scheduleModal.colDate" />
              </th>
              <th scope="col" className={styles.thAmount}>
                <FormattedMessage id="certificates.payment.scheduleModal.colAmount" />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className={row.isDeposit ? styles.rowDeposit : undefined}>
                <td className={styles.tdNum}>{row.num}</td>
                <td className={row.isDeposit ? styles.tdStrong : styles.tdPayment}>
                  {row.payment}
                </td>
                <td className={styles.tdDate}>{row.date}</td>
                <td className={row.isDeposit ? styles.tdStrongAmount : styles.tdAmount}>
                  ${row.amount.toFixed(2)}
                </td>
              </tr>
            ))}
            <tr className={styles.rowTotal}>
              <td colSpan={3} className={styles.tdTotalLabel}>
                <FormattedMessage id="certificates.payment.scheduleModal.total" />
              </td>
              <td className={styles.tdTotalValue}>${total.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
